import "rc-slider/assets/index.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "primereact/resources/primereact.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";

import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  AutocompleteItem,
  Divider,
  Input,
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { useStore } from "../../app/stores/store";
import { PagingParams } from "../../types/pagination";

type Props = {
  pageSize?: number;
};

const BookingFilter = ({ pageSize }: Props) => {
  const {
    bookingStore: {
      setDateFilter,
      setStatusFilter,
      setPaymentstatusFilter,
      setServiceFilter,
      setBookingCodeFilter,
      loadBookings,
      setPagingParams,
      fromDate: startDate,
      toDate: endDate,
      bookingStatus,
      paymentStatus,
      serviceId,
      bookingCode,
    },
    serviceStore: { servicesDropdown, loadServicesDropdown },
  } = useStore();

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const { t } = useTranslation();
  const [localBookingCode, setLocalBookingCode] = useState(bookingCode ?? "");

  const handleFilter = useCallback(() => {
    setDateFilter(fromDate, toDate);
    setPagingParams(new PagingParams(1, pageSize)); // Reset to first page
    loadBookings();
  }, [
    fromDate,
    toDate,
    setDateFilter,
    setPagingParams,
    loadBookings,
    pageSize,
  ]);

  useEffect(() => {
    // const now = new Date();
    // const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    //   .toISOString()
    //   .split("T")[0];

    setFromDate(startDate);
    setToDate(endDate);
  }, [startDate, endDate]);

  const handleStatusSelect = (status: string) => {
    setStatusFilter(status);
    loadBookings();
  };

  const handlePaymentStatusSelect = (paymentStatus: string) => {
    setPaymentstatusFilter(paymentStatus);
    loadBookings();
  };

  const handleBookingCodeSelect = (value: string) => {
    setLocalBookingCode(value);
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setBookingCodeFilter(localBookingCode.trim());
      setPagingParams(new PagingParams(1, pageSize));
      loadBookings();
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [
    localBookingCode,
    setBookingCodeFilter,
    setPagingParams,
    loadBookings,
    pageSize,
  ]);

  const handleServiceSelect = (service: string) => {
    setServiceFilter(service);
    loadBookings();
  };
  useEffect(() => {
    if (fromDate || toDate || bookingStatus || serviceId) {
      handleFilter();
    }
  }, [fromDate, toDate, bookingStatus, serviceId, handleFilter]);

  useEffect(() => {
    loadServicesDropdown();
  }, [loadServicesDropdown]);

  function handleFromDateChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFromDate(e.target.value);
  }

  function handleToDateChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setToDate(e.target.value);
  }

  const statusDefaultItems = [
    { label: "Pending", value: "Pending" },
    { label: "In Process", value: "InProcess" },
    { label: "Completed", value: "Completed" },
    { label: "Canceled", value: "Canceled" },
  ];

  const paymentStatusItems = [
    { label: "Paid", value: "Completed" },
    { label: "Unpaid", value: "Pending" },
  ];

  return (
    <div className="section-search page-search">
      <div className="container mt-10 px-4">
        <div
          className="
          bg-none
          w-full
          lg:w-1/2
          mx-auto
          flex
          flex-col
          items-center
          pt-6
        "
        >
          {/* Divider */}
          <Divider className="my-1 border-black-300 w-full" />

          {/* Filters Row */}
          <div
            className="
            flex
            flex-col
            sm:flex-row
            lg:flex-nowrap
            items-center
            justify-center
            lg:justify-between
            gap-4
            mt-4
            w-full
          "
          >
            {/* Booking Status Dropdown */}
            <select
              className="
              w-full
              sm:w-auto
              lg:w-40
              mb-4
              border-b-2
              text-black
              border-gray-300
              focus:border-blue-500
              focus:outline-none
              bg-transparent
              p-2
            "
              value={bookingStatus ?? ""}
              onChange={(e) => handleStatusSelect(e.target.value)}
            >
              <option value="">{t("Status")}</option>
              {statusDefaultItems.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            {/* Booking Payment status Dropdown */}
            <select
              className="
    w-full
    sm:w-auto
    lg:w-40
    mb-4
    border-b-2
    text-black
    border-gray-300
    focus:border-blue-500
    focus:outline-none
    bg-transparent
    p-2
  "
              value={paymentStatus ?? ""}
              onChange={(e) => handlePaymentStatusSelect(e.target.value)}
            >
              <option value="">{t("Payment Status")}</option>
              {paymentStatusItems.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            {/* From Date Input */}
            <input
              type="date"
              className="
              w-full
              sm:w-auto
              lg:w-36
              mb-4
              border-b-2
              text-black
              border-gray-300
              focus:border-blue-500
              focus:outline-none
              bg-transparent
              p-2
            "
              value={fromDate ?? ""}
              onChange={(e) => handleFromDateChange(e)}
            />

            {/* To Date Input */}
            <input
              type="date"
              className="
              w-full
              sm:w-auto
              lg:w-36
              mb-4
              border-b-2
              text-black
              border-gray-300
              focus:border-blue-500
              focus:outline-none
              bg-transparent
              p-2
            "
              value={toDate ?? ""}
              onChange={(e) => handleToDateChange(e)}
            />

            {/* Service Dropdown */}
            <select
              className="
              w-full
              sm:w-auto
              lg:w-48
              mb-4
              text-black
              border-b-2
              border-gray-300
              focus:border-blue-500
              focus:outline-none
              bg-transparent
              p-2
            "
              value={serviceId ?? ""}
              onChange={(e) => handleServiceSelect(e.target.value)}
            >
              <option value="">{t("All Services")}</option>
              {servicesDropdown
                ?.filter(
                  (item) =>
                    item.name === "Tourist Visa" ||
                    item.name === "VIP Pick & Drop" ||
                    item.name === "Tourist Visa KSA"
                )
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </select>

            {/* Booking Code Input */}
            <input
              type="text"
              className="
              w-full
              sm:w-auto
              lg:w-44
              mb-4
              border-b-2
              text-black
              border-gray-300
              focus:border-blue-500
              focus:outline-none
              bg-transparent
              p-2
            "
              placeholder="Booking Code"
              value={localBookingCode}
              onChange={(e) => handleBookingCodeSelect(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingFilter;
